import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { Upload } from 'grommet-icons';
import SignatureCanvas from 'react-signature-canvas';
import { AppButton, RadioButtonSlider } from '@Components/Control/Buttons';
import { useAppTheme } from '@Components/Context';

const HANDWRITTEN_FONTS = [
  'Segoe Script', 'Bradley Hand', 'Brush Script MT', 'cursive', 'sans-serif',
];

const SIGNATURE_BOX_WIDTH = 500;
const SIGNATURE_BOX_HEIGHT = 200;

const SignatureInput = ({
  small,
  submitLoading,
  signatureSubmitted,
  signaturePreview = null,
  finalSubmitted,
  isDetailsValid,
  onSignatureSubmit,
  onSignatureReset,
  uploadInputId = 'signature-upload-input',
  customMessage = null,
}) => {
  const { theme: { page, button } } = useAppTheme();

  const [sigMode, setSigMode] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [mediaSignature, setMediaSignature] = useState('');
  const [drawSignature, setDrawSignature] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);
  const sigPadRef = useRef(null);

  const [canvasDims, setCanvasDims] = useState({
    width: SIGNATURE_BOX_WIDTH,
    height: SIGNATURE_BOX_HEIGHT,
  });

  useEffect(() => {
    function handleResize() {
      setCanvasDims({
        width: small ? Math.min(window.innerWidth - 32, SIGNATURE_BOX_WIDTH) : SIGNATURE_BOX_WIDTH,
        height: SIGNATURE_BOX_HEIGHT,
      });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [small]);

  useEffect(() => {
    if (sigMode === 'draw' && sigPadRef.current && sigPadRef.current.isEmpty()) {
      setDrawSignature('');
    }
  }, [sigMode]);

  const getSignatureBase64 = () => {
    if (sigMode === 'draw') return drawSignature;
    if (sigMode === 'type') {
      if (!typedSignature) return '';
      const canvas = document.createElement('canvas');
      canvas.width = SIGNATURE_BOX_WIDTH;
      canvas.height = SIGNATURE_BOX_HEIGHT - 20;
      const ctx = canvas.getContext('2d');
      ctx.font = `36px ${HANDWRITTEN_FONTS.join(',')}`;
      ctx.fillStyle = '#000000';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, 20, SIGNATURE_BOX_HEIGHT / 2 - 10);
      return canvas.toDataURL();
    }
    if (sigMode === 'upload') return mediaSignature;
    return '';
  };

  const handleMediaSignature = (file) => {
    if (!file) { setMediaSignature(''); return; }
    const reader = new window.FileReader();
    reader.onload = (ev) => setMediaSignature(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrawEnd = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      setDrawSignature(sigPadRef.current.getTrimmedCanvas().toDataURL());
    } else {
      setDrawSignature('');
    }
  };

  const handleSigModeChange = (mode) => {
    if (!signatureSubmitted) setSigMode(mode);
  };

  const isSignatureValid = !!getSignatureBase64();
  const signatureControlsDisabled = signatureSubmitted;
  const canReset = (drawSignature || typedSignature || mediaSignature || signaturePreview)
    && !submitLoading && !finalSubmitted;

  const handleSignatureSubmit = () => {
    if (!isSignatureValid || !isDetailsValid) return;
    onSignatureSubmit(getSignatureBase64());
  };

  const handleSignatureReset = () => {
    if (submitLoading) return;
    setDrawSignature('');
    setTypedSignature('');
    setMediaSignature('');
    setFileInputKey((k) => k + 1);
    if (sigPadRef.current) sigPadRef.current.clear();
    if (onSignatureReset) onSignatureReset();
  };

  // --- Signature input and preview per mode ---
  let signatureInput = null;
  let signaturePreviewBox = null;

  if (signatureSubmitted && signaturePreview) {
    signaturePreviewBox = (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        border={{ color: button.primaryBg, size: '1px' }}
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
      >
        <img
          src={signaturePreview}
          alt="Signature preview"
          style={{ height: SIGNATURE_BOX_HEIGHT - 2, width: SIGNATURE_BOX_WIDTH - 2, borderRadius: 10 }}
        />
      </Box>
    );
  } else if (sigMode === 'draw') {
    signatureInput = (
      <Box
        background="#fff"
        round="var(--app-radius)"
        align="center"
        pad="2px"
        justify="center"
        width={small ? '100%' : `${canvasDims.width}px`}
        height={`${canvasDims.height}px`}
        style={{ maxWidth: '100%' }}
      >
        <SignatureCanvas
          ref={sigPadRef}
          penColor="#000000"
          canvasProps={{
            width: canvasDims.width,
            height: canvasDims.height,
            style: {
              borderRadius: 'var(--app-radius)',
              border: `1px solid ${button.primaryBg}`,
              width: '100%',
              maxWidth: '100%',
              height: `${canvasDims.height}px`,
              touchAction: 'none',
            },
            tabIndex: signatureControlsDisabled ? -1 : 0,
            disabled: signatureControlsDisabled,
            onMouseUp: handleDrawEnd,
            onTouchEnd: handleDrawEnd,
          }}
          onEnd={handleDrawEnd}
          clearOnResize={false}
          disabled={signatureControlsDisabled}
        />
      </Box>
    );
    signaturePreviewBox = drawSignature ? (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        border={{ color: button.primaryBg, size: '1px' }}
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
      >
        <img
          src={drawSignature}
          alt="Drawn signature preview"
          style={{ height: SIGNATURE_BOX_HEIGHT - 2, width: SIGNATURE_BOX_WIDTH - 2, borderRadius: 10 }}
        />
      </Box>
    ) : (
      <Box gap="0.5rem">
        <Text size="1rem" color={page.secondaryText} textAlign="center">Preview</Text>
        <Text color={page.secondaryText} size="1rem" style={{ opacity: 0.7 }} textAlign="center">
          Begin drawing your signature to view
        </Text>
      </Box>
    );
  } else if (sigMode === 'type') {
    signatureInput = (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
        border={{ color: button.primaryBg, size: '1px' }}
      >
        <input
          type="text"
          value={typedSignature}
          onChange={(e) => setTypedSignature(e.target.value)}
          placeholder="Type your full name"
          disabled={signatureControlsDisabled}
          style={{
            fontSize: '1.35rem',
            fontWeight: 600,
            border: 'none',
            outline: 'none',
            width: '100%',
            textAlign: 'center',
            background: 'transparent',
            color: '#000000',
            fontFamily: HANDWRITTEN_FONTS.join(','),
          }}
        />
      </Box>
    );
    signaturePreviewBox = typedSignature ? (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        border={{ color: button.primaryBg, size: '1px' }}
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
      >
        <Text size="2rem" color="#000000" style={{ fontFamily: HANDWRITTEN_FONTS.join(','), fontWeight: 600 }}>
          {typedSignature}
        </Text>
      </Box>
    ) : (
      <Box gap="0.5rem">
        <Text size="1rem" color={page.secondaryText} textAlign="center">Preview</Text>
        <Text color={page.secondaryText} size="1rem" style={{ opacity: 0.7 }} textAlign="center">
          Type your signature in the box
        </Text>
      </Box>
    );
  } else if (sigMode === 'upload') {
    signatureInput = (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        border={{ color: button.primaryBg, size: '1px' }}
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
        gap="small"
        style={{ cursor: signatureControlsDisabled ? 'not-allowed' : 'pointer', position: 'relative' }}
        onClick={() => {
          if (!signatureControlsDisabled) {
            document.getElementById(uploadInputId)?.click();
          }
        }}
      >
        <input
          key={fileInputKey}
          id={uploadInputId}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          disabled={signatureControlsDisabled}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            handleMediaSignature(file);
          }}
        />
        <Upload size="2rem" color={button.primaryBg} />
        <Text size="1rem" color={page.secondaryText} style={{ opacity: 0.8, marginTop: '0.5rem', textAlign: 'center' }}>
          {mediaSignature
            ? (document.getElementById(uploadInputId)?.files?.[0]?.name || 'Image selected')
            : 'No image selected'}
        </Text>
      </Box>
    );
    signaturePreviewBox = mediaSignature ? (
      <Box
        background="#fff"
        round="var(--app-radius)"
        pad="medium"
        border={{ color: button.primaryBg, size: '1px' }}
        align="center"
        justify="center"
        width={small ? '100%' : `${SIGNATURE_BOX_WIDTH}px`}
        height={`${SIGNATURE_BOX_HEIGHT}px`}
      >
        <img
          src={mediaSignature}
          alt="Signature preview"
          style={{ height: SIGNATURE_BOX_HEIGHT - 2, width: SIGNATURE_BOX_WIDTH - 2, borderRadius: 10 }}
        />
      </Box>
    ) : (
      <Box gap="0.5rem">
        <Text size="1rem" color={page.secondaryText} textAlign="center">Preview</Text>
        <Text color={page.secondaryText} size="1rem" style={{ opacity: 0.7 }} textAlign="center">
          Select an image file to view
        </Text>
      </Box>
    );
  }

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Electronic Signature</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Select your preferred signing method below.
        </Text>
      </Box>
      <Box width="fit-content">
        <RadioButtonSlider
          radioOptions={[
            { label: 'Draw Signature', value: 'draw', disabled: signatureControlsDisabled },
            { label: 'Type Signature', value: 'type', disabled: signatureControlsDisabled },
            { label: 'Upload Image', value: 'upload', disabled: signatureControlsDisabled },
          ]}
          textColor={page.primaryText}
          componentBg={page.cardBg}
          activeButtonBg={button.primaryBg}
          updateParent={handleSigModeChange}
          value={sigMode}
          disabled={signatureControlsDisabled}
        />
      </Box>
      <Box
        direction={small ? 'column' : 'row'}
        gap="medium"
        width="100%"
        align={small ? 'stretch' : 'center'}
        justify="start"
      >
        <Box flex>{signatureInput}</Box>
        <Box flex align="center" justify="center">{signaturePreviewBox}</Box>
      </Box>
      <Box
        width="100%"
        direction="row"
        align="center"
        justify={small ? 'center' : 'start'}
        margin={{ top: '0.5rem' }}
        gap="small"
      >
        <AppButton
          overrideHover
          onClick={handleSignatureReset}
          level="tableControls"
          disabled={!canReset}
          bgColor={button.primaryBg}
          color={button.primaryText}
          label="Reset Signature"
        />
        <AppButton
          overrideHover
          onClick={handleSignatureSubmit}
          level="tableControls"
          disabled={!isSignatureValid || !isDetailsValid || signatureSubmitted}
          bgColor={button.primaryBg}
          color={button.primaryText}
          label="Submit Signature"
        />
      </Box>
      {customMessage ? (
        <Text size="0.85rem" color={page.secondaryText} margin={{ top: '-0.5rem' }}>
          {customMessage}
        </Text>
      ) : (
        <Text size="0.85rem" color={page.secondaryText} margin={{ top: '-0.5rem' }}>
          Your electronic signature is legally binding and timestamped upon submission. Once you are
          satisfied with your signature and selections, click &quot;Finish&quot; to proceed to account
          activation.
        </Text>
      )}
    </Box>
  );
};

SignatureInput.propTypes = {
  small: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  signatureSubmitted: PropTypes.bool.isRequired,
  signaturePreview: PropTypes.string,
  finalSubmitted: PropTypes.bool.isRequired,
  isDetailsValid: PropTypes.bool.isRequired,
  onSignatureSubmit: PropTypes.func.isRequired,
  onSignatureReset: PropTypes.func.isRequired,
  uploadInputId: PropTypes.string,
  customMessage: PropTypes.string,
};

export default SignatureInput;
